"use client"

import type { DataImportSource } from "@/schema/data-import-source"
import { Loader2 } from "lucide-react"

interface SourceExtractionProgressProps {
  source: DataImportSource
  sourceName: string
}

function formatSummaryItems(items: Array<{ count: number | null; label: string }>) {
  return items
    .flatMap((item) => (item.count != null && item.count > 0 ? [`${item.count.toLocaleString()} ${item.label}`] : []))
    .join(" · ")
}

export function SourceExtractionProgress({ source, sourceName }: SourceExtractionProgressProps) {
  const statistics = source.statistics
  const isScraping = source.status === "scraping"
  const isFinalizing = source.status === "ready"
  const isCheckingWealthboxVerification =
    (source.import_workflows || source.import_opportunities) &&
    source.workflow_scrape_authenticated_at == null &&
    source.workflow_scrape_completed_at == null

  const primarySummary =
    statistics.households == null
      ? "Calculating counts…"
      : formatSummaryItems([
          { count: statistics.households, label: "households" },
          { count: statistics.people, label: "people" },
          { count: statistics.businesses, label: "businesses" },
        ])
  const secondarySummary = formatSummaryItems([
    { count: statistics.tasks, label: "tasks" },
    { count: statistics.notes, label: "notes" },
    { count: statistics.events, label: "meetings" },
    { count: statistics.opportunities, label: "opportunities" },
  ])

  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-6 py-12">
      <Loader2 className="text-muted-foreground h-16 w-16 animate-spin" />

      <div className="text-center">
        <h3 className="text-lg font-medium">
          {isScraping
            ? `Downloading data from ${sourceName}`
            : isFinalizing
              ? "Preparing your import for review"
              : "Processing your data"}
        </h3>
        <p className="text-muted-foreground mt-1">
          {isScraping
            ? "This may take several minutes while we export your data"
            : isFinalizing
              ? "We staged your data and are setting up the review flow now."
              : primarySummary || "Extracting records..."}
        </p>
        {!isScraping && primarySummary && !isFinalizing && (
          <p className="text-muted-foreground mt-1 text-sm">{primarySummary}</p>
        )}
        {secondarySummary && <p className="text-muted-foreground mt-1 text-sm">{secondarySummary}</p>}
        {isFinalizing && primarySummary && <p className="text-muted-foreground mt-1 text-sm">{primarySummary}</p>}
      </div>

      <p className="text-muted-foreground mt-4 text-xs">
        {isCheckingWealthboxVerification
          ? `Keep this dialog open while we check whether ${sourceName} verification is required.`
          : "You can close this dialog. The import will continue in the background."}
      </p>
    </div>
  )
}
